/**
 * workspaceStore — Team workspaces (V2 additive).
 *
 * The active workspace is EITHER "personal" (activeOrgId === null → the app
 * behaves exactly as before) OR a Team org. When a team workspace is active,
 * every workspace-scoped API request carries `workspace_org_id` (injected by
 * the shared axios interceptor in src/api/workspaceContext.ts) so the backend
 * reads/writes org-owned rows and spends the org's pooled credits.
 *
 * The orgs themselves ride along on get-user-info (userStore.user.organizations);
 * this store only holds WHICH one is active, persisted across reloads.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Organization, User } from '@/types';

interface WorkspaceState {
  /** null = personal workspace (default — the app exactly as before). */
  activeOrgId: string | null;

  setActiveOrg: (orgId: string | null) => void;
  /** Drop a stale selection (org removed / membership revoked / org expired). */
  reconcileWithUser: (user: User | null) => void;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      activeOrgId: null,

      setActiveOrg: (orgId) => set({ activeOrgId: orgId }),

      reconcileWithUser: (user) => {
        const { activeOrgId } = get();
        if (!activeOrgId) return;
        const stillValid = (user?.organizations || []).some(
          (o) => o.orgId === activeOrgId && o.orgStatus !== 'expired',
        );
        if (!stillValid) set({ activeOrgId: null });
      },
    }),
    {
      name: 'vivideo-workspace-store',
      partialize: (state) => ({ activeOrgId: state.activeOrgId }),
    },
  ),
);

/** The active org object (or null in the personal workspace). */
export function getActiveOrg(user: User | null | undefined): Organization | null {
  const activeOrgId = useWorkspaceStore.getState().activeOrgId;
  if (!activeOrgId) return null;
  return (user?.organizations || []).find((o) => o.orgId === activeOrgId) || null;
}

/** Reactive variant for components (subscribes to the active id). */
export function useActiveOrg(user: User | null | undefined): Organization | null {
  const activeOrgId = useWorkspaceStore((s) => s.activeOrgId);
  if (!activeOrgId) return null;
  return (user?.organizations || []).find((o) => o.orgId === activeOrgId) || null;
}
